import React from 'react';
import styled from 'styled-components';

const WhySection = styled.section`
  width: 100%;
  padding: ${props => props.theme.spacing.mega} 0;
  background: ${props => props.theme.colors.background.primary};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.large};
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: ${props => props.theme.fontSizes.hero};
  margin-bottom: ${props => props.theme.spacing.xxlarge};
  color: ${props => props.theme.colors.text.primary};
  line-height: 1.3;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSizes.xxlarge};
  }
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xlarge};
`;

const FeatureCard = styled.div`
  background: linear-gradient(
    135deg,
    rgba(26, 30, 53, 0.8) 0%,
    rgba(18, 18, 18, 0.8) 100%
  );
  border: 2px solid ${props => props.theme.colors.border.secondary};
  border-radius: ${props => props.theme.borderRadius.xlarge};
  padding: ${props => props.theme.spacing.xxlarge};
  position: relative;
  overflow: hidden;
  transition: all ${props => props.theme.transitions.medium};

  /* Borde superior con gradiente gaming */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${props => props.theme.colors.primary} 0%,
      ${props => props.theme.colors.neon.cyan} 50%,
      ${props => props.theme.colors.neon.purple} 100%
    );
  }

  /* Efecto de glow en el fondo */
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(90, 234, 165, 0.1) 0%,
      transparent 70%
    );
    opacity: 0;
    transition: opacity ${props => props.theme.transitions.slow};
    pointer-events: none;
  }

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.glow};
    transform: translateY(-5px);

    &::after {
      opacity: 1;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.xlarge};
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
`;

const FeatureTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xxlarge};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.medium};
  font-weight: ${props => props.theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 1px;

  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.primaryLight} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSizes.xlarge};
  }
`;

const FeatureDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.8;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSizes.medium};
  }
`;

interface Feature {
  title: string;
  description: string;
}

const WhyNeuralPrompt: React.FC = () => {
  const features: Feature[] = [
    {
      title: 'Customized Copy/Paste Function',
      description: 'Stop indefinitely typing the imagine command over and over with every new change in your prompt; NEURALPROMPT does it for you. Additionally, Use the cloning functionality of the editor in NEURALPROMPT to modify previous scripts and implement new ideas in Midjourney'
    },
    {
      title: 'Parameters Catalog',
      description: "Take advantage of NEURALPROMPT's flexibility to create new Midjourney's parameters and values for your prompts. Each parameter can be customized to your specific needs."
    },
    {
      title: 'Prompt Library Repository',
      description: 'NEURALPROMPT provides you with an integrated repository to save and organize your prompts. Easily access previous work, edit them, or use them as a foundation for new projects.'
    }
  ];

  return (
    <WhySection>
      <Container>
        <SectionTitle>
          Discover why NeuralPrompt is the ideal companion<br />
          when creating prompts for Midjourney
        </SectionTitle>

        <FeaturesContainer>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <CardContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </CardContent>
            </FeatureCard>
          ))}
        </FeaturesContainer>
      </Container>
    </WhySection>
  );
};

export default WhyNeuralPrompt;
